import { useRef, useState, type ComponentType, type ReactElement, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'

import {
  IconCheckboxOff,
  IconCheckboxOn,
  IconChevronLeft,
  IconDrag,
  IconWBriefcase,
  IconWCalendar,
  IconWCampus,
  IconWCompass,
  IconWContacts,
  IconWDiscount,
  IconWEvents,
  IconWGlobe,
  IconWNews,
  IconWStudent,
  IconWTools,
} from '@/components/ui/icons'
import { useWidgetsStore, type WidgetId } from '@/stores/widgets.store'
import { cn } from '@/lib/utils'

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>

const WIDGET_ICONS: Record<WidgetId, IconComponent> = {
  news: IconWNews,
  events: IconWEvents,
  tools: IconWTools,
  navigator: IconWCompass,
  calendar: IconWCalendar,
  contacts: IconWContacts,
  studentOffice: IconWCampus,
  doItOnline: IconWGlobe,
  discount: IconWDiscount,
  myStudies: IconWStudent,
  scholarships: IconWBriefcase,
}

interface EditWidgetsTabProps {
  onBack: () => void
}

/**
 * "Edit widgets" — Figma `widgets` list (2265:290022). Rows carry a drag
 * handle, a checkbox and the widget icon; dragging reorders the list and the
 * lifted row gets the design's elevation.
 *
 * Uses the native HTML5 drag API, so pointer input works everywhere; on touch
 * devices reordering falls back to the keyboard controls on the handle.
 */
export function EditWidgetsTab({ onBack }: EditWidgetsTabProps): ReactElement {
  const { t } = useTranslation()
  const order = useWidgetsStore((state) => state.order)
  const enabled = useWidgetsStore((state) => state.enabled)
  const toggle = useWidgetsStore((state) => state.toggle)
  const move = useWidgetsStore((state) => state.move)

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const dragFrom = useRef<number | null>(null)

  const handleKeyDown = (event: React.KeyboardEvent, index: number): void => {
    if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault()
      move(index, index - 1)
    }
    if (event.key === 'ArrowDown' && index < order.length - 1) {
      event.preventDefault()
      move(index, index + 1)
    }
  }

  return (
    <>
      <div data-name="panel-list" className="hub-rule flex w-full items-start gap-2 pb-4">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('actions.back')}
          className="text-btn-icon hover:bg-btn-secondary-raised flex shrink-0 items-center justify-center rounded-xs p-1.5 transition-colors"
        >
          <IconChevronLeft className="size-[16px]" aria-hidden="true" />
        </button>
        <h2 className="hub-headline-lg text-list-text whitespace-nowrap">
          {t('widget.editWidgets')}
        </h2>
      </div>

      <ul data-name="widgets" className="flex w-full flex-col gap-4">
        {order.map((id, index) => {
          const Icon = WIDGET_ICONS[id]
          const checked = enabled.includes(id)
          const dragging = draggingIndex === index

          return (
            <li
              key={id}
              data-name="panel-list"
              draggable
              onDragStart={() => {
                dragFrom.current = index
                setDraggingIndex(index)
              }}
              onDragOver={(event) => {
                event.preventDefault()
                const from = dragFrom.current
                if (from === null || from === index) return
                move(from, index)
                dragFrom.current = index
                setDraggingIndex(index)
              }}
              onDragEnd={() => {
                dragFrom.current = null
                setDraggingIndex(null)
              }}
              className={cn(
                'hub-rule flex w-full items-center gap-2 pb-4 transition-colors',
                !dragging && 'hover:bg-btn-secondary-raised/50',
                dragging && 'bg-panel-surface shadow-[0px_4px_8px_rgba(0,0,0,0.08)]',
              )}
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={t('widget.reorder')}
                onKeyDown={(event) => {
                  handleKeyDown(event, index)
                }}
                className="text-list-text flex size-[24px] shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
              >
                <IconDrag className="size-[24px]" aria-hidden="true" />
              </span>

              {/* Everything right of the handle is one target. */}
              <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={t(`widget.names.${id}`)}
                onClick={() => {
                  toggle(id)
                }}
                className="flex min-w-px flex-1 items-center gap-2 text-left"
              >
                <span className="text-list-text flex size-[24px] shrink-0 items-center justify-center">
                  {checked ? (
                    <IconCheckboxOn className="size-[24px]" aria-hidden="true" />
                  ) : (
                    <IconCheckboxOff className="size-[24px]" aria-hidden="true" />
                  )}
                </span>

                <Icon className="text-list-text size-[24px] shrink-0" aria-hidden="true" />

                <span className="hub-title-lg text-list-text min-w-px flex-1">
                  {t(`widget.names.${id}`)}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </>
  )
}
